import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, List, Popconfirm, Space, Tabs, Tag, Typography, message } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import LoadingCard from "components/LoadingCard/LoadingCard";
import ListSearch from "components/ListSearch/ListSearch";
import { useTextFilter } from "helpers/textFilter";
import { CONTROL } from "pages/MarketingPages/FieldInput";
import { buildPostUrl, mergePage, nextActions } from "./commentHelpers";
import {
  deleteComment,
  errorMessage,
  fetchCommentCounts,
  fetchComments,
  PAGE_SIZE,
  setCommentStatus,
} from "./commentsApi";

dayjs.extend(relativeTime);

const { Text, Paragraph } = Typography;

const STATUSES = ["pending", "approved", "rejected"];

const commentSearchFields = (comment) => [comment.name, comment.email, comment.body, comment.post?.title];

const STATUS_TAG = {
  pending: { color: "gold", label: "Чакащ" },
  approved: { color: "green", label: "Одобрен" },
  rejected: { color: "red", label: "Отхвърлен" },
};

/**
 * Blog comment moderation queue (DAB-842).
 *
 * One tab per status, each fetched and counted independently so switching
 * tabs never shows stale rows from another status. Every action (approve,
 * reject, delete) reloads both the active tab and the counts — there is no
 * optimistic update, so a slow network never leaves the badge counts lying.
 */
function Comments() {
  const [activeTab, setActiveTab] = useState("pending");
  const [comments, setComments] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const loadCounts = useCallback(async () => {
    try {
      setCounts(await fetchCommentCounts());
    } catch (error) {
      // The tab labels just keep their last known counts; the list below
      // still shows its own load error if that call also fails.
      message.error(errorMessage(error));
    }
  }, []);

  /**
   * Where the next page starts. Counts rows the *server* has handed over, not
   * rows kept — `mergePage` drops duplicates, so `comments.length` would drift
   * behind the window and eventually re-request rows the list already holds.
   */
  const skipRef = useRef(0);
  /**
   * Which fetch the screen is currently showing. A page-1 load claims a new
   * id, so an in-flight `loadMore` from the tab that was open a moment ago
   * knows to throw its result away instead of merging another status's rows
   * into this one.
   */
  const requestRef = useRef(0);

  const loadComments = useCallback(async (status) => {
    const requestId = (requestRef.current += 1);
    setIsLoading(true);
    setLoadError(null);
    try {
      const rows = await fetchComments(status, { skip: 0 });
      if (requestRef.current !== requestId) return;
      skipRef.current = rows.length;
      setComments(rows);
      // A short page is the last page. Asking the badge instead would leave
      // the button up forever whenever a count and the rows disagree.
      setHasMore(rows.length === PAGE_SIZE);
    } catch (error) {
      if (requestRef.current !== requestId) return;
      setLoadError(errorMessage(error));
    } finally {
      if (requestRef.current === requestId) setIsLoading(false);
    }
  }, []);

  /**
   * Append the next page. The API caps every request, so a busy queue is
   * reached a page at a time rather than in one unbounded query.
   */
  const loadMore = useCallback(async () => {
    const requestId = requestRef.current;
    setIsLoadingMore(true);
    try {
      const next = await fetchComments(activeTab, { skip: skipRef.current });
      if (requestRef.current !== requestId) return;
      skipRef.current += next.length;
      setComments((current) => mergePage(current, next));
      setHasMore(next.length === PAGE_SIZE);
    } catch (error) {
      if (requestRef.current === requestId) message.error(errorMessage(error));
    } finally {
      if (requestRef.current === requestId) setIsLoadingMore(false);
    }
  }, [activeTab]);

  const reload = useCallback(async () => {
    await Promise.all([loadComments(activeTab), loadCounts()]);
  }, [activeTab, loadComments, loadCounts]);

  useEffect(() => {
    loadComments(activeTab);
  }, [activeTab, loadComments]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  const changeStatus = async (comment, status) => {
    setBusyId(comment._id);
    try {
      await setCommentStatus(comment._id, status);
      message.success(
        status === "approved" ? "Коментарът е одобрен" : "Коментарът е отхвърлен",
      );
      await reload();
    } catch (error) {
      message.error(errorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (comment) => {
    setBusyId(comment._id);
    try {
      await deleteComment(comment._id);
      message.success("Коментарът е изтрит");
      await reload();
    } catch (error) {
      message.error(errorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  const emptyText = {
    pending: "Няма чакащи коментари.",
    approved: "Няма одобрени коментари.",
    rejected: "Няма отхвърлени коментари.",
  };

  // The comments API has no search parameter, so this only narrows the pages
  // already loaded — the placeholder says so.
  const visibleComments = useTextFilter(comments, commentSearchFields, searchQuery);

  return (
    <div style={{ padding: 16, maxWidth: 720, margin: "0 auto" }}>
      <Space direction="vertical" size={4} style={{ width: "100%", marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Коментари
        </Typography.Title>
        <Text type="secondary">
          Преглеждаш коментарите под статиите, преди да се появят на сайта.
        </Text>
      </Space>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={STATUSES.map((status) => ({
          key: status,
          label: `${STATUS_TAG[status].label} (${counts[status] ?? 0})`,
        }))}
      />

      {isLoading && <LoadingCard />}

      {!isLoading && !loadError && (
        <ListSearch
          value={searchQuery}
          onSearch={setSearchQuery}
          placeholder="Търсене в заредените коментари"
          className="mb-3"
        />
      )}

      {!isLoading && loadError && (
        <Alert
          type="error"
          showIcon
          message="Неуспешно зареждане на коментарите"
          description={loadError}
          action={<Button onClick={() => loadComments(activeTab)}>Опитай отново</Button>}
        />
      )}

      {!isLoading && !loadError && (
        <List
          bordered
          dataSource={visibleComments}
          locale={{
            emptyText: searchQuery ? "Няма коментари, отговарящи на търсенето." : emptyText[activeTab],
          }}
          loadMore={
            hasMore ? (
              <div style={{ textAlign: "center", padding: 12 }}>
                <Button loading={isLoadingMore} onClick={loadMore}>
                  Покажи още
                </Button>
              </div>
            ) : null
          }
          renderItem={(comment) => {
            const { canApprove, canReject } = nextActions(comment.status);
            const postUrl = buildPostUrl(comment.post);
            const isBusy = busyId === comment._id;

            return (
              <List.Item style={{ display: "block", padding: 16 }}>
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                  <Space wrap size={8} style={{ justifyContent: "space-between", width: "100%" }}>
                    <Space wrap size={8}>
                      <Tag color={STATUS_TAG[comment.status]?.color}>
                        {STATUS_TAG[comment.status]?.label ?? comment.status}
                      </Tag>
                      <Text type="secondary">
                        {comment.createdAt ? dayjs(comment.createdAt).fromNow() : ""}
                      </Text>
                    </Space>
                  </Space>

                  <Text strong>
                    {comment.name}
                    {comment.email ? (
                      <Text type="secondary"> &lt;{comment.email}&gt;</Text>
                    ) : null}
                  </Text>

                  <Paragraph
                    style={{ margin: 0 }}
                    ellipsis={{ rows: 3, expandable: true, symbol: "още" }}
                  >
                    {comment.body}
                  </Paragraph>

                  {comment.post ? (
                    postUrl ? (
                      <a href={postUrl} target="_blank" rel="noreferrer">
                        {comment.post.title}
                      </a>
                    ) : (
                      <Text type="secondary">{comment.post.title}</Text>
                    )
                  ) : (
                    <Text type="secondary" italic>
                      Статията е премахната
                    </Text>
                  )}

                  <Space wrap size={8}>
                    {canApprove && (
                      <Button
                        type="primary"
                        style={CONTROL}
                        loading={isBusy}
                        onClick={() => changeStatus(comment, "approved")}
                      >
                        Одобри
                      </Button>
                    )}
                    {canReject && (
                      <Button
                        style={CONTROL}
                        loading={isBusy}
                        onClick={() => changeStatus(comment, "rejected")}
                      >
                        Отхвърли
                      </Button>
                    )}
                    <Popconfirm
                      title="Сигурен ли си, че искаш да изтриеш този коментар?"
                      okText="Изтрий"
                      cancelText="Отказ"
                      onConfirm={() => remove(comment)}
                    >
                      <Button danger style={CONTROL} loading={isBusy}>
                        Изтрий
                      </Button>
                    </Popconfirm>
                  </Space>
                </Space>
              </List.Item>
            );
          }}
        />
      )}
    </div>
  );
}

export default Comments;
